import {
  Badge,
  Box,
  Button,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import {
  FILTER_CONTROL_SIZE,
  FilterBar,
  FilterDateRange,
  FilterField,
  FilterSearch,
  FilterSelect
} from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { BulkActionBar, SelectAllCheckbox, SelectRowCheckbox, useRowSelection } from '../../../components/RowSelection'
import { FormSelect } from '../../../components/Select'
import ShortText from '../../../components/ShortText'
import { REVIEW_STATUS, StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDate, formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import { EntregaDeTrabalhoService } from '../../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { EntregaDeTrabalhoReviewModal } from './EntregaDeTrabalhoReviewModal'
import { EntregaDeTrabalhoViewModal } from './EntregaDeTrabalhoViewModal'
import { courseFormat, secondsToTime, showAlert } from './helper'
import { EEntregaDeTrabalhoType, IEntregaDeTrabalho, IListFilters, IReviewTimeStatistics, IStatistics } from './models'

const INITIAL_FILTERS: IListFilters = {
  status: '',
  is_validated_by_secretary: '',
  search: '',
  teacher: '',
  type: '',
  course: { format: '', name: '' },
  created_at: { from: '', to: '' }
}

const EMPTY_COURSE = JSON.stringify(INITIAL_FILTERS.course)

export default function EntregaDeTrabalhosPage() {
  const service = new EntregaDeTrabalhoService()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const toast = useAppToast()
  const [statistics, setStatistics] = useState<IStatistics>()
  const [reviewStatistics, setReviewStatistics] = useState<IReviewTimeStatistics>()
  const [entregaDeTrabalhos, setEntregaDeTrabalhos] = useState<IEntregaDeTrabalho[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [length, setLength] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [draw, setDraw] = useState(0)
  const [modalId, setModalId] = useState<number>()
  const [toggleApprovalWorkSubmission, setToggleApprovalWorkSubmission] = useState<IEntregaDeTrabalho>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const {
    isOpen: toggleApprovalDialogIsOpen,
    onOpen: toggleApprovalDialogOnOpen,
    onClose: toggleApprovalDialogIsClose
  } = useDisclosure()
  const [deleteId, SetDeleteId] = useState<number>()
  const [teachers, setTeachers] = useState<string[]>([])
  const [courses, setCourses] = useState<{ format: string; name: string }[]>([])
  const selection = useRowSelection(entregaDeTrabalhos)
  const {
    isOpen: deleteMultipleDialogIsOpen,
    onOpen: deleteMultipleDialogOnOpen,
    onClose: deleteMultipleDialogOnClose
  } = useDisclosure()
  const {
    isOpen: secretaryToggleApprovalMultipleDialogIsOpen,
    onOpen: secretaryToggleApprovalMultipleDialogOnOpen,
    onClose: secretaryToggleApprovalMultipleDialogOnClose
  } = useDisclosure()
  const [filters, setFilters] = useState<IListFilters>(INITIAL_FILTERS)
  const isDirty =
    filters.status !== '' ||
    filters.is_validated_by_secretary !== '' ||
    filters.search !== '' ||
    filters.teacher !== '' ||
    filters.type !== '' ||
    filters.course.name !== '' ||
    filters.created_at.from !== '' ||
    filters.created_at.to !== ''
  // Derivado, e não um `useState` atualizado por efeito: com estado próprio o rótulo do botão
  // ficava um render atrás da seleção.
  const isAllSelectedItemsApproved = !entregaDeTrabalhos.some(
    (work) => selection.isSelected(work.id) && work.is_validated_by_secretary === false
  )
  const hasRole = useHasRole()
  const permissions = {
    validateAssignments: hasRole('validate_assignments'),
    deleteAssignments: hasRole('delete_assignments')
  }

  // `is_validated_by_secretary` decide se a ação valida ou invalida; a cor e o rótulo
  // saem do registry (`validate`/`invalidate`), não de um ternário por diálogo.
  const isInvalidating = toggleApprovalWorkSubmission?.is_validated_by_secretary === true

  const handleDelete = async () => {
    if (deleteId === undefined) {
      return
    }

    setIsSubmitting(true)
    try {
      await service.delete(deleteId)
      toast.success('Entrega de trabalho excluída')
      deleteDialogIsClose()
      loadEntregasDeTrabalho()
    } catch (error) {
      toast.error('Erro ao excluir a entrega de trabalho', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteSelected = async () => {
    setIsSubmitting(true)
    try {
      await service.deleteMultiple(selection.selectedIds)
      toast.success('Entregas de trabalho excluídas')
      deleteMultipleDialogOnClose()
      selection.clear()
      loadEntregasDeTrabalho()
    } catch (error) {
      toast.error('Erro ao excluir as entregas de trabalho', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleApproval = async () => {
    if (!toggleApprovalWorkSubmission) {
      return
    }

    setIsSubmitting(true)
    try {
      if (isInvalidating) {
        await service.secretaryDisapprove(toggleApprovalWorkSubmission.id)
        toast.success('Entrega de trabalho invalidada')
      } else {
        await service.secretaryApprove(toggleApprovalWorkSubmission.id)
        toast.success('Entrega de trabalho validada')
      }
      toggleApprovalDialogIsClose()
      loadEntregasDeTrabalho()
    } catch (error) {
      toast.error(
        isInvalidating ? 'Erro ao invalidar a entrega de trabalho' : 'Erro ao validar a entrega de trabalho',
        error
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleApprovalSelected = async () => {
    const isApproving = !isAllSelectedItemsApproved

    setIsSubmitting(true)
    try {
      if (isApproving) {
        await service.secretaryApproveMultiple(selection.selectedIds)
        toast.success('Entregas de trabalho validadas')
      } else {
        await service.secretaryDisapproveMultiple(selection.selectedIds)
        toast.success('Entregas de trabalho invalidadas')
      }
      secretaryToggleApprovalMultipleDialogOnClose()
      // A validação em massa não muda os ids da listagem, então a poda do `useRowSelection` não roda:
      // sem isto as linhas seguem marcadas e a barra passa a oferecer a ação inversa sobre elas.
      selection.clear()
      loadEntregasDeTrabalho()
    } catch (error) {
      toast.error(
        isApproving ? 'Erro ao validar as entregas de trabalho' : 'Erro ao invalidar as entregas de trabalho',
        error
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const loadEntregasDeTrabalho = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const leadsList = await service.list({
        draw,
        length,
        start: (page - 1) * length,
        order: [{ column: 0, dir: 'desc' }],
        filters
      })

      setDraw(draw + 1)

      setTotalPages(Math.ceil(leadsList.recordsFiltered / length))

      setEntregaDeTrabalhos(leadsList.data)
      loadStatistics()
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as entregas de trabalho', error)
    } finally {
      setLoading(false)
    }
  }

  const loadStatistics = async () => {
    setStatistics(await service.loadStatistics(filters))
    setReviewStatistics(await service.loadReviewTimeStatistics(filters))
  }

  useEffect(() => {
    loadEntregasDeTrabalho()
  }, [page, length, filters])

  useEffect(() => {
    loadTeachers()
    loadCourses()
  }, [])

  const handleOpenReviewModal = async (id: number) => {
    setModalId(id)
    setIsReviewModalOpen(true)
  }

  const handleOpenModal = async (id: number) => {
    setModalId(id)
    setIsModalOpen(true)
  }

  const handleDeleteDialog = (id: number) => {
    deleteDialogOnOpen()
    SetDeleteId(id)
  }

  const toggleSecretaryApproval = async (workSubmission: IEntregaDeTrabalho) => {
    toggleApprovalDialogOnOpen()
    setToggleApprovalWorkSubmission(workSubmission)
  }

  const loadTeachers = async () => {
    const teachersList = await service.getTeachers()
    setTeachers(teachersList)
  }

  const loadCourses = async () => {
    const coursesList = await service.getCourses()
    setCourses(coursesList)
  }

  return (
    <>
      <Flex mb={3} gap={4} align="center" wrap="wrap" direction={{ base: 'column', md: 'row' }}>
        <Box flex={{ base: '1 1 100%', md: '1 1 50%' }}>
          <PageTitle title="Entregas de Trabalho" description="Trabalhos enviados pelos alunos para correção" mb="0" />
        </Box>
        <Flex flex={{ base: '1 1 100%', md: '0 0 auto' }} align="center" justify="flex-end">
          <Box textAlign="right">
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              fontWeight="bold"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Contagem
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Total de entregas: {statistics?.total ?? '-'}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Pendentes: {statistics?.pendente ?? '-'}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Aprovados: {statistics?.aprovado ?? '-'}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Reprovados: {statistics?.reprovado ?? '-'}
            </Text>
          </Box>
        </Flex>
        <Box flex={{ base: '1 1 100%', md: '1 1 33%' }}>
          <Table>
            <Tbody>
              <Tr>
                <Th></Th>
                <Th>Sem Atraso</Th>
                <Th>Com Atraso</Th>
              </Tr>
              <Tr>
                <Th>Revisados</Th>
                <Td>{reviewStatistics?.reviewed.not_delayed}</Td>
                <Td>{reviewStatistics?.reviewed.delayed}</Td>
              </Tr>
              <Tr>
                <Th>Pendentes</Th>
                <Td>{reviewStatistics?.not_reviewed.not_delayed}</Td>
                <Td>{reviewStatistics?.not_reviewed.delayed}</Td>
              </Tr>
              <Tr>
                <Th>Tempo médio</Th>
                <Td colSpan={2}>{secondsToTime(parseInt(reviewStatistics?.average_time_in_seconds || '0'))}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </Flex>
      <FilterBar onClear={() => setFilters(INITIAL_FILTERS)} isDirty={isDirty}>
        <FilterSearch
          value={filters.search}
          onChange={(search) => setFilters({ ...filters, search })}
          placeholder="Buscar entrega"
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => setFilters({ ...filters, status: status as IListFilters['status'] })}
          options={[
            { value: 'PENDENTE', label: 'Pendente' },
            { value: 'APROVADO', label: 'Aprovado' },
            { value: 'REPROVADO', label: 'Reprovado' }
          ]}
        />
        <FilterSelect
          label="Status Secretária"
          value={filters.is_validated_by_secretary}
          onChange={(value) =>
            setFilters({ ...filters, is_validated_by_secretary: value as IListFilters['is_validated_by_secretary'] })
          }
          options={[
            { value: '1', label: 'Sim' },
            { value: '0', label: 'Não' }
          ]}
        />
        <FilterSelect
          label="Professor"
          value={filters.teacher}
          onChange={(teacher) => setFilters({ ...filters, teacher })}
          options={teachers.map((teacher) => ({ value: teacher, label: teacher }))}
        />
        <FilterField label="Curso">
          {/* O filtro é o par formato+nome, então o valor da opção é o objeto serializado. */}
          <FormSelect
            size={FILTER_CONTROL_SIZE}
            value={JSON.stringify(filters.course)}
            onChange={(value) => setFilters({ ...filters, course: JSON.parse(value) })}
            options={[
              { value: EMPTY_COURSE, label: 'Todos' },
              ...courses
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((course) => ({
                  value: JSON.stringify(course),
                  label: `${course.name} - ${courseFormat[course.format as 'presencial' | 'online' | 'online-ao-vivo']}`
                }))
            ]}
            hideEmptyOption
          />
        </FilterField>
        <FilterSelect
          label="Tipo"
          value={filters.type}
          onChange={(type) => setFilters({ ...filters, type: type as IListFilters['type'] })}
          options={[
            { value: 'pesquisa-de-campo', label: 'Pesquisa de campo' },
            { value: 'trabalho-de-conclusão', label: 'Trabalho de conclusão' }
          ]}
        />
        <FilterDateRange
          label="Data de criação"
          value={{ start: filters.created_at.from, end: filters.created_at.to }}
          onChange={(date) => setFilters({ ...filters, created_at: { from: date.start, to: date.end } })}
        />
      </FilterBar>

      <BulkActionBar selection={selection} entity="entrega" feminine>
        {permissions.validateAssignments && (
          <Button
            size="sm"
            colorScheme={isAllSelectedItemsApproved ? 'green' : 'darkGray'}
            leftIcon={<Icon name={isAllSelectedItemsApproved ? 'lni lni-checkmark' : 'lni lni-hourglass'} size="md" />}
            onClick={secretaryToggleApprovalMultipleDialogOnOpen}
          >
            {isAllSelectedItemsApproved ? 'Secretária: invalidar' : 'Secretária: validar'}
          </Button>
        )}
        {permissions.deleteAssignments && (
          <Button
            size="sm"
            colorScheme="red"
            leftIcon={<Icon name="lni lni-trash-can" size="md" />}
            onClick={deleteMultipleDialogOnOpen}
          >
            Excluir
          </Button>
        )}
      </BulkActionBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <SelectAllCheckbox selection={selection} />
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Curso</Th>
              <Th>Professor</Th>
              <Th>Tipo</Th>
              <Th textAlign="center">Nota</Th>
              <Th>Status</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={10} />
            ) : loadError ? (
              <TableErrorRow colSpan={10} entity="entregas" feminine onRetry={loadEntregasDeTrabalho} />
            ) : entregaDeTrabalhos.length === 0 ? (
              <TableEmptyRow colSpan={10} entity="entrega" feminine />
            ) : (
              entregaDeTrabalhos.map((workSubmission) => (
                <Tr key={workSubmission.id} background={workSubmission.is_validated_by_secretary ? 'gray.100' : ''}>
                  <SelectRowCheckbox selection={selection} id={workSubmission.id} />
                  <Td>{workSubmission.id}</Td>
                  <Td>
                    <ShortText text={workSubmission.name} beforeChars={20} afterChars={0} />
                  </Td>
                  <Td>
                    <ShortText
                      text={`${workSubmission.course} - ${
                        courseFormat[workSubmission.format as 'presencial' | 'online' | 'online-ao-vivo']
                      }`}
                      beforeChars={30}
                      afterChars={0}
                    />
                  </Td>
                  <Td>
                    <ShortText text={workSubmission.teacher} beforeChars={20} afterChars={0} />
                  </Td>
                  <Td>{EEntregaDeTrabalhoType[workSubmission.type]}</Td>
                  <Td textAlign="center">
                    {workSubmission.review_grade ? (
                      <Tooltip
                        label={`Validado por ${workSubmission.review_teacher} em ${formatDateTime(
                          workSubmission.review_created_at + 'Z'
                        ).replace(' ', ' às ')}h`}
                      >
                        <Text>{Number(workSubmission.review_grade).toFixed(2)}</Text>
                      </Tooltip>
                    ) : (
                      <Tooltip label="Clique para avaliar" aria-label="Clique para avaliar">
                        <Badge
                          colorScheme="gray"
                          rounded="full"
                          onClick={() => {
                            handleOpenReviewModal(workSubmission.id)
                          }}
                          cursor="pointer"
                        >
                          Avaliar
                        </Badge>
                      </Tooltip>
                    )}
                  </Td>
                  <Td>
                    {workSubmission.time_taken_seconds ? (
                      <Tooltip label={`Tempo de revisão: ${secondsToTime(workSubmission.time_taken_seconds)}`}>
                        <StatusBadge status={workSubmission.status} statuses={REVIEW_STATUS} />
                      </Tooltip>
                    ) : (
                      <StatusBadge status={workSubmission.status} statuses={REVIEW_STATUS} />
                    )}
                  </Td>
                  <Td>
                    <Flex alignItems="center">
                      {formatDateTime(workSubmission.created_at)}{' '}
                      {showAlert(
                        workSubmission.created_at,
                        workSubmission.review_created_at,
                        workSubmission.status === 'PENDENTE'
                      )}
                    </Flex>
                  </Td>
                  <Td>
                    <Button
                      onClick={() => handleOpenModal(workSubmission.id)}
                      colorScheme="magenta"
                      variant="outline"
                      size="xs"
                      mb="2"
                      ml="2"
                      mr="0"
                    >
                      <Icon name="lni lni-eye" />
                    </Button>
                    <Link
                      to={`/entrega-de-trabalho/preview/${workSubmission.public_id}`}
                      target="_blank"
                      rel="noopener"
                    >
                      <Button colorScheme="magenta" variant="outline" size="xs" mb="2" ml="2" mr="0">
                        <Icon name="lni lni-link" />
                      </Button>
                    </Link>
                    {permissions.validateAssignments && (
                      <>
                        {workSubmission.is_validated_by_secretary ? (
                          <Tooltip
                            label={
                              <>
                                Validado por{' '}
                                <strong>{workSubmission.validated_by_secretary_user_name || 'não identificado'}</strong>{' '}
                                em{' '}
                                <strong>
                                  {workSubmission.validated_by_secretary_at
                                    ? formatDate(workSubmission.validated_by_secretary_at)
                                    : 'data não registrada'}
                                </strong>
                              </>
                            }
                            aria-label="Invalidar Secretária"
                            placement="left"
                          >
                            <Button
                              onClick={() => toggleSecretaryApproval(workSubmission)}
                              colorScheme="green"
                              size="xs"
                              mb="2"
                              ml="2"
                              mr="0"
                            >
                              <Icon name="lni lni-checkmark" />
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip label="Secretária: Validar" aria-label="Validar Secretária" placement="left">
                            <Button
                              onClick={() => toggleSecretaryApproval(workSubmission)}
                              colorScheme="darkGray"
                              size="xs"
                              mb="2"
                              ml="2"
                              mr="0"
                            >
                              <Icon name="lni lni-hourglass" />
                            </Button>
                          </Tooltip>
                        )}
                      </>
                    )}

                    {permissions.deleteAssignments && (
                      <Button
                        onClick={() => handleDeleteDialog(workSubmission.id)}
                        colorScheme="magenta"
                        size="xs"
                        mb="2"
                        ml="2"
                        mr="0"
                      >
                        <Icon name="lni lni-trash-can" />
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={length}
        onPageSizeChange={(pageSize) => {
          setLength(pageSize)
          setPage(1)
        }}
      />

      <EntregaDeTrabalhoViewModal
        id={modalId}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          loadEntregasDeTrabalho()
        }}
      />
      <EntregaDeTrabalhoReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false)
          loadEntregasDeTrabalho()
        }}
        id={modalId}
      />
      <ConfirmDialog
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        onConfirm={handleDelete}
        action="delete"
        entity="entrega de trabalho"
        feminine
        isLoading={isSubmitting}
      />
      <ConfirmDialog
        isOpen={deleteMultipleDialogIsOpen}
        onClose={deleteMultipleDialogOnClose}
        onConfirm={handleDeleteSelected}
        action="delete"
        count={selection.count}
        entity="entregas de trabalho"
        isLoading={isSubmitting}
      />
      <ConfirmDialog
        isOpen={toggleApprovalDialogIsOpen}
        onClose={toggleApprovalDialogIsClose}
        onConfirm={handleToggleApproval}
        action={isInvalidating ? 'invalidate' : 'validate'}
        entity="entrega de trabalho"
        feminine
        isLoading={isSubmitting}
      />
      <ConfirmDialog
        isOpen={secretaryToggleApprovalMultipleDialogIsOpen}
        onClose={secretaryToggleApprovalMultipleDialogOnClose}
        onConfirm={handleToggleApprovalSelected}
        action={isAllSelectedItemsApproved ? 'invalidate' : 'validate'}
        count={selection.count}
        entity="entregas de trabalho"
        isLoading={isSubmitting}
      />

      <ActionFab label="Nova entrega de trabalho" to="/entrega-de-trabalho/enviar" openInNewTab />
    </>
  )
}
